/**
 * Synthetic 8,760-hour solar production (DESIGN §4.2).
 *
 * Ported from ES `src/solar/synthetic.ts`: astronomical plane-of-array curve
 * (declination, hour angle, zenith, incidence, Kasten–Young air mass,
 * Meinel DNI, isotropic diffuse, ground reflection), normalised to sum to 1.0.
 * Texas changes: latitude and clearness come from the region; defaults are
 * tilt 25°, azimuth 180° (south). The curve is scaled by
 * `systemKwDc × regionYield`, or by an installer's annual estimate.
 *
 * Pure: no DOM, network or global state.
 */
import { HOURS_IN_YEAR, sumByMonth } from "./calendar";

/** Solar parameters the engine needs (subset of `RegionConfig`). */
export interface SolarClimate {
  readonly latitude: number;
  readonly solarClearness: number;
  readonly defaultYieldKwhPerKw: number;
}

/**
 * Normalised 8,760 POA curve (sums to 1.0, strictly 0 at night).
 * Hour-of-day is treated as solar time (no longitude / DST shift), as in ES.
 */
export function generateSolarCurve(
  latitudeDeg: number,
  clearness: number,
  tiltDeg = 25,
  azimuthDeg = 180,
): Float64Array {
  const curve = new Float64Array(HOURS_IN_YEAR);
  const d2r = Math.PI / 180;
  const r2d = 180 / Math.PI;
  const lat = latitudeDeg * d2r;
  const tilt = tiltDeg * d2r;
  const surfAz = (azimuthDeg - 180) * d2r;
  let total = 0;

  for (let hour = 0; hour < HOURS_IN_YEAR; hour++) {
    const dayOfYear = Math.floor(hour / 24) + 1;
    const hod = hour % 24;
    const decl = 23.45 * d2r * Math.sin((360 / 365) * (284 + dayOfYear) * d2r);
    const omega = (hod + 0.5 - 12) * 15 * d2r;
    const cosZ = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(omega);
    if (cosZ <= 0) continue;
    const zen = Math.acos(Math.min(1, Math.max(0, cosZ)));
    const zenDeg = zen * r2d;
    if (zenDeg >= 90) continue;

    const sinZ = Math.sin(zen);
    let solAz = 0;
    if (sinZ > 0.001) {
      const c = (cosZ * Math.sin(lat) - Math.sin(decl)) / (sinZ * Math.cos(lat));
      solAz = Math.acos(Math.min(1, Math.max(-1, c)));
      if (omega < 0) solAz = -solAz;
    }
    const cosInc = cosZ * Math.cos(tilt) + sinZ * Math.sin(tilt) * Math.cos(solAz - surfAz);
    if (cosInc <= 0) continue;

    const airMass = 1 / (cosZ + 0.50572 * Math.pow(96.07995 - zenDeg, -1.6364));
    const am = Math.max(1, Math.min(airMass, 20));
    const dni = 1.367 * Math.pow(0.7, Math.pow(am, 0.678));
    const dhi = 0.15 * dni * cosZ;
    const poa =
      dni * cosInc + (dhi * (1 + Math.cos(tilt))) / 2 + ((dni * cosZ + dhi) * 0.2 * (1 - Math.cos(tilt))) / 2;
    const seasonal = 1 + 0.08 * Math.cos(((dayOfYear - 190) * 2 * Math.PI) / 365);
    const v = Math.max(0, poa) * clearness * seasonal;
    if (v > 0) {
      curve[hour] = v;
      total += v;
    }
  }

  if (total > 0) {
    const inv = 1 / total;
    for (let i = 0; i < HOURS_IN_YEAR; i++) curve[i] *= inv;
  }
  return curve;
}

export interface SolarOptions {
  /** System size, kWdc. Ignored when `annualKwhOverride` is given. */
  readonly systemKwDc?: number;
  /** Installer's annual production estimate, kWh/yr (rep override). */
  readonly annualKwhOverride?: number;
  /** Override the region's default specific yield, kWh/kWdc·yr. */
  readonly yieldKwhPerKwOverride?: number;
  readonly tiltDeg?: number;
  readonly azimuthDeg?: number;
}

export interface SolarResult {
  /** kWh produced each hour. */
  readonly hourly: Float64Array;
  readonly monthlyKwh: number[];
  readonly annualKwh: number;
  /** How annual production was determined. */
  readonly basis: "override" | "kw_x_yield";
}

/** Annual production: override if given, else `systemKwDc × yield`. */
export function annualProductionKwh(climate: SolarClimate, o: SolarOptions): number {
  if (o.annualKwhOverride !== undefined) {
    if (!Number.isFinite(o.annualKwhOverride) || o.annualKwhOverride < 0) {
      throw new Error(`annualKwhOverride must be non-negative, got ${o.annualKwhOverride}`);
    }
    return o.annualKwhOverride;
  }
  const kw = o.systemKwDc ?? 0;
  const yld = o.yieldKwhPerKwOverride ?? climate.defaultYieldKwhPerKw;
  if (!Number.isFinite(kw) || kw < 0) throw new Error(`systemKwDc must be non-negative, got ${kw}`);
  if (!Number.isFinite(yld) || yld < 0) throw new Error(`yield must be non-negative, got ${yld}`);
  return kw * yld;
}

/** `solar(h) = curve(h; lat, tilt, az, clearness) × annualProductionKwh`. */
export function generateSolarProfile(climate: SolarClimate, o: SolarOptions): SolarResult {
  const annual = annualProductionKwh(climate, o);
  const curve = generateSolarCurve(climate.latitude, climate.solarClearness, o.tiltDeg ?? 25, o.azimuthDeg ?? 180);
  const hourly = new Float64Array(HOURS_IN_YEAR);
  for (let h = 0; h < HOURS_IN_YEAR; h++) hourly[h] = curve[h] * annual;
  return {
    hourly,
    monthlyKwh: sumByMonth(hourly),
    annualKwh: annual,
    basis: o.annualKwhOverride !== undefined ? "override" : "kw_x_yield",
  };
}
